const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

/**
 * Download a PDF from the backend PDF export API.
 *
 * @param {string} endpoint  - Relative API path, e.g. "/pdf/exam/123"
 * @param {string} filename  - Suggested download filename, e.g. "exam_123.pdf"
 * @param {Object} [options]
 * @param {boolean} [options.includeAnswerKey] - Append ?includeAnswerKey=true (admin only)
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<void>}
 */
export async function downloadPdf(endpoint, filename, options = {}) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }

  let url = `${API_BASE_URL}${endpoint}`;
  if (options.includeAnswerKey) {
    url += '?includeAnswerKey=true';
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    signal: options.signal,
  });

  if (!response.ok) {
    let errorMessage = 'Failed to generate PDF';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // response is not JSON
      errorMessage = `${errorMessage} (Status: ${response.status})`;
    }
    throw new Error(errorMessage);
  }

  // Check if response is actually a PDF
  const contentType = response.headers.get('content-type');
  if (contentType && !contentType.includes('application/pdf')) {
    // Backend returned non-PDF content (likely error JSON)
    let errorMessage = 'Backend returned non-PDF response';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || `Unexpected response type: ${contentType}`;
    } catch {
      errorMessage = `Unexpected response type: ${contentType}`;
    }
    throw new Error(errorMessage);
  }

  const blob = await response.blob();
  
  // Additional validation: check blob size
  if (blob.size === 0) {
    throw new Error('Generated PDF is empty (0 bytes). Backend may have failed silently.');
  }
  
  if (blob.size < 100) {
    console.warn(`PDF file is suspiciously small (${blob.size} bytes). It may be corrupted.`);
  }

  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(a);
}

// ── Convenience helpers ──────────────────────────────────────────────

export function downloadExamPdf(examId, examTitle, includeAnswerKey = false) {
  const safeTitle = (examTitle || examId).replace(/[^a-zA-Z0-9_-]/g, '_');
  const suffix = includeAnswerKey ? '_answers' : '';
  return downloadPdf(
    `/pdf/exam/${examId}`,
    `${safeTitle}${suffix}.pdf`,
    { includeAnswerKey },
  );
}

export function downloadExerciseDayPdf(exerciseId, dayNumber, exerciseTitle, includeAnswerKey = false) {
  const safeTitle = (exerciseTitle || exerciseId).replace(/[^a-zA-Z0-9_-]/g, '_');
  const suffix = includeAnswerKey ? '_answers' : '';
  return downloadPdf(
    `/pdf/exercise/${exerciseId}/day/${dayNumber}`,
    `${safeTitle}_day${dayNumber}${suffix}.pdf`,
    { includeAnswerKey },
  );
}

export function downloadExerciseSectionPdf(dayId, section, title, includeAnswerKey = false) {
  const safeTitle = (title || dayId).replace(/[^a-zA-Z0-9_-]/g, '_');
  const suffix = includeAnswerKey ? '_answers' : '';
  const sectionLabel = section === 'lc-exercise' ? 'LC' : section === 'rc-exercise' ? 'RC' : 'Attendance';
  return downloadPdf(
    `/pdf/exercise/day/${dayId}/section/${section}`,
    `${sectionLabel}_${safeTitle}${suffix}.pdf`,
    { includeAnswerKey },
  );
}
